// Loads the connected agents + scopes for a known vault and hydrates per-agent
// manifest cards (name/icon/description) from the marketplace API. Lives in
// `lib/vault/` so the vault page (and any future surface) shares the load path.

import { useCallback, useEffect, useState } from 'react';
import type { VaultSDK, AgentConnection, Scope } from '@cef-ai/vault-sdk';
import type { CereWallet } from '@/lib/clients/cere-wallet/connect';
import type { AgentCard } from '@/src/data/agent-cards';
import { useConfig } from '@/src/config/context';
import { cardFromManifest, fetchManifest } from './manifest-cards';
import { fetchAgentWidgets } from './agent-bucket-manifest';

export interface AgentsAndCardsState {
  agents: AgentConnection[];
  agentCards: Record<string, AgentCard>;
  scopes: Scope[];
  error: string | null;
  /**
   * False until the first auto-fetch resolves (success OR error), so the
   * caller can distinguish "still loading" from "fetch complete, no
   * agents." Lets the dashboard show a loader instead of flashing the
   * empty-state pitch on landing. Stays true across later refreshes.
   */
  loaded: boolean;
  refreshScopes: () => Promise<void>;
  refreshAgents: () => Promise<void>;
}

interface Inputs {
  wallet: CereWallet | null;
  sdk: VaultSDK | null;
  vaultBaseUrl: string | null;
  vaultId: string | null;
}

/**
 * Effect-driven loader for the vault dashboard's primary data set. Returns the
 * agent list, the card metadata for every agent, the scope list, and any error string. The
 * effect refires whenever wallet/baseUrl/vaultId changes; in-flight requests
 * are cancellation-checked so a stale response can't overwrite newer state.
 *
 * Also exposes `refreshScopes` and `refreshAgents` as stable callbacks so
 * dialogs can re-fetch after a mutation without remounting the page.
 */
export function useAgentsAndCards({ wallet, sdk, vaultBaseUrl, vaultId }: Inputs): AgentsAndCardsState {
  const { marketplaceBaseUrl, ddcCdnUrl, ddcComputeUrl } = useConfig();
  const [agents, setAgents] = useState<AgentConnection[]>([]);
  const [agentCards, setAgentCards] = useState<Record<string, AgentCard>>({});
  const [scopes, setScopes] = useState<Scope[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  const refreshScopes = useCallback(async () => {
    if (!wallet || !sdk || !vaultBaseUrl || !vaultId) return;
    try {
      const vault = await sdk.vault.current();
      const next = await vault.scopes.list().catch(() => [] as Scope[]);
      setScopes(next);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [wallet, sdk, vaultBaseUrl, vaultId]);

  const refreshAgents = useCallback(async () => {
    if (!wallet || !sdk || !vaultBaseUrl || !vaultId) return;
    try {
      const vault = await sdk.vault.current();
      const agentHandles = await vault.agents.list();

      // `AgentConnectionHandle` implements the `AgentConnection` shape, so
      // we can pass them straight through to the dashboard.
      const items = [...agentHandles];
      const cards: Record<string, AgentCard> = {};
      // Hydrate name/icon/description (marketplace card) + widgets (DDC bucket
      // manifest — the card endpoint is card-only) for every agent in parallel.
      const cardEntries = await Promise.all(
        items.map(async (item) => {
          // agentId is `<pubkey>:<alias>`; empty alias when malformed → the
          // guard in fetchAgentWidgets skips the fetch rather than 404-ing.
          const colon = item.agentId.indexOf(':');
          const alias = colon >= 0 ? item.agentId.slice(colon + 1) : '';
          const [m, widgets] = await Promise.all([
            fetchManifest(marketplaceBaseUrl, item.agentId),
            fetchAgentWidgets({ orchestratorUrl: ddcComputeUrl, cdnUrl: ddcCdnUrl, asPubKey: item.asPubKey, alias, version: item.version }),
          ]);
          if (!m && widgets.length === 0) return null;
          const card = cardFromManifest(m ?? {}, item.agentId);
          return [item.agentId, widgets.length > 0 ? { ...card, widgets } : card] as const;
        }),
      );
      for (const entry of cardEntries) if (entry) cards[entry[0]] = entry[1];

      setAgents(items);
      setAgentCards(cards);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [wallet, sdk, vaultBaseUrl, vaultId, marketplaceBaseUrl, ddcCdnUrl, ddcComputeUrl]);

  useEffect(() => {
    if (!wallet || !sdk || !vaultBaseUrl || !vaultId) return;
    let cancelled = false;
    (async () => {
      // Flip `loaded` to true once the initial fetch settles — success or
      // error. The error string is exposed separately; the loader's only
      // job is to suppress the empty-state pitch until we know the truth.
      try {
        await Promise.all([refreshScopes(), refreshAgents()]);
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [refreshScopes, refreshAgents, wallet, sdk, vaultBaseUrl, vaultId]);

  return { agents, agentCards, scopes, error, loaded, refreshScopes, refreshAgents };
}
